import React, { FunctionComponent, useState, useEffect, useCallback } from "react";
import omit from "ramda/src/omit";

import Consumers from "./Customers";
import { useQuery } from "@apollo/client";

import {
  GetTradingSettings,
  GetTradingSettingsVariables,
} from "graphql/generated";
import { GET_TRADING_SETTINGS_QUERY } from "graphql/queries";
import { Heading } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { IItem } from "components/settings-table/types";
import SettingsFormPage from "../../components/SettingsFormPage";

interface IProps {
  siteId: string;
  uiVersion?: string | null;
}

export interface ITradingConsumer {
  id: number;
  factory_id: number;
  title: string;
  price_heat: number;
  price_electro: number;
}

interface ITrading {
  consumers: ITradingConsumer[];
}


const Trading: FunctionComponent<IProps> = ({
  siteId,
  uiVersion,
}) => {
  const intl = useIntl();

  const { data, loading } = useQuery<GetTradingSettings, GetTradingSettingsVariables>(
    GET_TRADING_SETTINGS_QUERY,
    {
      variables: { siteId },
      errorPolicy: "all",
    }
  );

  const [trading, setTrading] = useState<ITrading>();

  const setConsumer = useCallback((index: number, consumer: ITradingConsumer) => {
    console.log("TOP", index, consumer)
    // if (trading) {
    //   trading.consumers[index] = consumer;
    //   setTrading({
    //     consumers: trading.consumers,
    //   });
    // }
  }, []);

  useEffect(() => {
    if (!data) {
      return;
    }

    setTrading({
      consumers: data.getTradingSettings.consumers.map(_ => omit(["__typename"], _) as unknown as ITradingConsumer),
    });
  }, [data]);

  const settingsItems = (uiVersion: string | null | undefined): Array<IItem> => {
    switch (uiVersion) {
      case "pv-only": return [
        {
          id: "electricityBuyDistributionPrice",
          name: intl.formatMessage({
            id: "settings__trading__electricity__buy_distribution_price",
            defaultMessage: "Electricity; Distribution price",
          }),
          type: "float",
          unit: "€/MWh",
          decimals: 2,
        },
        {
          id: "electricityBuySystemServicesPrice",
          name: intl.formatMessage({
            id: "settings__trading__electricity__buy_system_services_price",
            defaultMessage: "Electricity; System services price",
          }),
          type: "float",
          unit: "€/MWh",
          decimals: 2,
        },
        {
          id: "electricityBuyRenewableEnergyPrice",
          name: intl.formatMessage({
            id: "settings__trading__electricity__buy_renewable_energy_price",
            defaultMessage: "Electricity; Renewable energy price",
          }),
          type: "float",
          unit: "€/MWh",
          decimals: 2,
        },
        {
          id: "electricityBuyEnergyTax",
          name: intl.formatMessage({
            id: "settings__trading__electricity__buy_tax",
            defaultMessage: "Electricity; Tax from electricity",
          }),
          type: "float",
          unit: "€/MWh",
          decimals: 2,
        },
        {
          id: "electricityOperatorFee",
          name: intl.formatMessage({
            id: "settings__trading__electricity__operator_fee",
            defaultMessage: "Electricity; Operator monthly fee",
          }),
          type: "float",
          unit: "€/m",
          decimals: 2,
        },
        {
          id: "electricityReservedCapacityFee",
          name: intl.formatMessage({
            id: "settings__trading__electricity__reserved_capacity_fee",
            defaultMessage: "Electricity; Reserved capacity monthly fee",
          }),
          type: "float",
          unit: "€/m",
          decimals: 2,
        },
        {
          id: "electricitySellFixedPrice",
          name: intl.formatMessage({
            id: "settings__trading__electricity__sell_fixed_price",
            defaultMessage: "Electricity; Fixed selling price",
          }),
          type: "float",
          unit: "€/MWh",
          decimals: 2,
        },
      ]
      default: return [
        {
          id: "batteryCyclePrice",
          name: intl.formatMessage({
            id: "settings__trading__battery__cycle_price",
            defaultMessage: "Battery; Cycle price",
          }),
          type: "float",
          unit: "€",
        },
        {
          id: "batteryTwoDayCycle",
          name: intl.formatMessage({
            id: "settings__trading__battery__two_day_cycle",
            defaultMessage: "Battery; Two-day cycle",
          }),
          type: "bool",
        },
        {
          id: "batteryGreedyDischarge",
          name: intl.formatMessage({
            id: "settings__trading__battery__greedy_discharge",
            defaultMessage: "Battery; Greedy (dis)charge",
          }),
          type: "bool",
        },
        {
          id: "batteryCapacityMultiplier",
          name: intl.formatMessage({
            id: "settings__trading__battery__capacity_multiplier",
            defaultMessage: "Battery; Capacity multiplier",
          }),
          type: "float",
          unit: "%",
        },
        {
          id: "batteryStaticLoC",
          name: intl.formatMessage({
            id: "settings__trading__battery__static_length_of_charge",
            defaultMessage: "Battery; Static length of charge",
          }),
          type: "int",
          unit: "h",
        },
        {
          id: "batteryStaticLoD",
          name: intl.formatMessage({
            id: "settings__trading__battery__static_length_of_discharge",
            defaultMessage: "Battery; Static length of discharge",
          }),
          type: "int",
          unit: "h",
        },
        {
          id: "electricityBuyDistributionPrice",
          name: intl.formatMessage({
            id: "settings__trading__electricity__buy_distribution_price",
            defaultMessage: "Electricity; Distribution price",
          }),
          type: "float",
          unit: "€/MWh",
          decimals: 2,
        },
        {
          id: "electricityBuySystemServicesPrice",
          name: intl.formatMessage({
            id: "settings__trading__electricity__buy_system_services_price",
            defaultMessage: "Electricity; System services price",
          }),
          type: "float",
          unit: "€/MWh",
          decimals: 2,
        },
        {
          id: "electricityBuyRenewableEnergyPrice",
          name: intl.formatMessage({
            id: "settings__trading__electricity__buy_renewable_energy_price",
            defaultMessage: "Electricity; Renewable energy price",
          }),
          type: "float",
          unit: "€/MWh",
          decimals: 2,
        },
        {
          id: "electricityBuyEnergyTax",
          name: intl.formatMessage({
            id: "settings__trading__electricity__buy_tax",
            defaultMessage: "Electricity; Tax from electricity",
          }),
          type: "float",
          unit: "€/MWh",
          decimals: 2,
        },
        {
          id: "electricityOperatorFee",
          name: intl.formatMessage({
            id: "settings__trading__electricity__operator_fee",
            defaultMessage: "Electricity; Operator monthly fee",
          }),
          type: "float",
          unit: "€/m",
          decimals: 2,
        },
        {
          id: "electricityReservedCapacityFee",
          name: intl.formatMessage({
            id: "settings__trading__electricity__reserved_capacity_fee",
            defaultMessage: "Electricity; Reserved capacity monthly fee",
          }),
          type: "float",
          unit: "€/m",
          decimals: 2,
        },
        {
          id: "electricityReservedCapacityBreachPenaltyPrice",
          name: intl.formatMessage({
            id: "settings__trading__electricity__reserved_capacity_breach_penalty_price",
            defaultMessage: "Electricity; Reserved capacity breach penalty price",
          }),
          type: "float",
          unit: "€/MW",
        },
        {
          id: "electricityBuyFixedPrice",
          name: intl.formatMessage({
            id: "settings__trading__electricity__buy_fixed_price",
            defaultMessage: "Electricity; Fixed buying price",
          }),
          type: "float",
          unit: "€/MWh",
          decimals: 2,
        },
        {
          id: "electricityBuyShiftPrice",
          name: intl.formatMessage({
            id: "settings__trading__electricity__buy_shift_price",
            defaultMessage: "Electricity; Buy price shift",
          }),
          type: "float",
          unit: "€/MWh",
          decimals: 2,
        },
        {
          id: "electricitySellFixedPrice",
          name: intl.formatMessage({
            id: "settings__trading__electricity__sell_fixed_price",
            defaultMessage: "Electricity; Fixed selling price",
          }),
          type: "float",
          unit: "€/MWh",
          decimals: 2,
        },
        {
          id: "electricitySellShiftPrice",
          name: intl.formatMessage({
            id: "settings__trading__electricity__sell_shift_price",
            defaultMessage: "Electricity; Sell price shift",
          }),
          type: "float",
          unit: "€/MWh",
          decimals: 2,
        },

        {
          id: "gasBuyDistributionPrice",
          name: intl.formatMessage({
            id: "settings__trading__gas__buy_distribution_price",
            defaultMessage: "Gas; Distribution price",
          }),
          type: "float",
          unit: "€/MWh",
          decimals: 2,
        },
        {
          id: "gasBuyAccountingPrice",
          name: intl.formatMessage({
            id: "settings__trading__gas__buy_accounting_price",
            defaultMessage: "Gas; Accounting price",
          }),
          type: "float",
          unit: "€/MWh",
          decimals: 2,
        },
        {
          id: "gasBuyEnergyTax",
          name: intl.formatMessage({
            id: "settings__trading__gas__buy_tax",
            defaultMessage: "Gas; Tax from gas",
          }),
          type: "float",
          unit: "€/MWh",
          decimals: 2,
        },
        {
          id: "gasOperatorFee",
          name: intl.formatMessage({
            id: "settings__trading__gas__operator_fee",
            defaultMessage: "Gas; Operator monthly fee",
          }),
          type: "float",
          unit: "€/m",
          decimals: 2,
        },
        {
          id: "gasBuyReservedCapacityFee",
          name: intl.formatMessage({
            id: "settings__trading__gas__buy_reserved_capacity_fee",
            defaultMessage: "Gas; Reserved capacity monthly fee",
          }),
          type: "float",
          unit: "€/m",
          decimals: 2,
        },
        {
          id: "gasBuyFixedPrice",
          name: intl.formatMessage({
            id: "settings__trading__gas__buy_fixed_price",
            defaultMessage: "Gas; Fixed buying price",
          }),
          type: "float",
          unit: "€/MWh",
          decimals: 2,
        },
        {
          id: "gasBuyShiftPrice",
          name: intl.formatMessage({
            id: "settings__trading__gas__buy_shift_price",
            defaultMessage: "Gas; Buy price shift",
          }),
          type: "float",
          unit: "€/MWh",
          decimals: 2,
        },
      ]
    }
  }

  return (
    <>
      <SettingsFormPage
        siteId={siteId}
        guideId="trading"
        uiVersion={uiVersion}
        settingsId="trading"
        settings={data?.getTradingSettings}
        isLoading={loading}
        items={settingsItems(uiVersion)}
      >
      {uiVersion === "default" && trading && (<>
        <Heading size="md" mt={6} mb={4}>
          {intl.formatMessage({
            id: "settings__trading__consumers___title",
            defaultMessage: "Consumers",
          })}
        </Heading>
        <Consumers
          uiVersion={uiVersion}
          data={trading.consumers}
          setConsumer={setConsumer}
          // settingsProps={settingsProps}
        />
      </>)}
      </SettingsFormPage>
    </>
  );
};

export default Trading;
